import {
    observable, action, makeObservable, runInAction, computed
} from "mobx";
import type RootStore from "@stores/RootStore";
import { AxiosError } from "axios";
import AuthorizationService from "../services/AuthorizationService";
import User from "../models/User";
import { UpdateAuthorizationHeaderConfig } from "@api/clients/httpClient";
import { getLocalStorageItem, setLocalStorageItem } from "@utils/localStorageUtil";
import { accessTokenKey, refreshTokenKey } from "@const/localStorage";
import AddressType from "@components/user-address/models/AddressType";

export default class AuthorizationStore {
    rootStore: RootStore;
    service: AuthorizationService;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        this.service = new AuthorizationService();

        makeObservable(this, {
            //#region UI
            isLoading: observable,
            setIsLoading: action,
            //#endregion

            user: observable,
            setUser: action,
            isUserLoggedIn: computed,
            authorizeUserAsync: action,
        });
    }

    //#region UI

    isLoading = true;
    setIsLoading = (value: boolean) => {
        this.isLoading = value;
    };

    //#endregion

    user: User | null = null;
    setUser = (user: User | null) => {
        this.user = user;
    };

    get isUserLoggedIn() {
        return this.user !== undefined && this.user !== null;
    }

    authorizeUserAsync = async (isRetry = false): Promise<boolean> => {
        this.setIsLoading(true);

        const accessToken = await getLocalStorageItem(accessTokenKey);
        if (accessToken === null || accessToken === "") {
            this.setIsLoading(false);
            return false;
        }

        await UpdateAuthorizationHeaderConfig();
        try {
            const authorizationResponse = await this.service.authorizeUserAsync();

            if (authorizationResponse.status === 200 && authorizationResponse.data !== null) {
                const user = authorizationResponse.data;
                this.setUser(user);
                this.rootStore.orderEditViewStore.setUserAddress(user.userAddresses?.find(a => a.type === AddressType.home));
                return true;
            }
        } catch (ex) {
            if (ex instanceof AxiosError) {
                if (ex.response?.status === 401 && !isRetry) {
                    const refreshToken = await getLocalStorageItem(refreshTokenKey);

                    if (refreshToken) {
                        const refreshResponse = await this.rootStore.authenticationStore.service.refreshTokenAsync(refreshToken);

                        if (refreshResponse.status === 200 && refreshResponse.data.access_token) {
                            setLocalStorageItem(accessTokenKey, refreshResponse.data.access_token);
                            setLocalStorageItem(refreshTokenKey, refreshResponse.data.refresh_token);
                            await UpdateAuthorizationHeaderConfig();
                            return await this.authorizeUserAsync(true);
                        }
                    }
                }
            }
            setLocalStorageItem(accessTokenKey, "");
            setLocalStorageItem(refreshTokenKey, "");
            this.setUser(null);
            this.rootStore.toastStore.alert(
                "warning",
                "Neočekivana greška prilikom prijave",
                "Molimo osvježi aplikaciju kako bismo te ponovo pokušali prijaviti.",
                10000
            );
        } finally {
            this.setIsLoading(false);
        }

        return false;
    };
}